/**
 * AnimationStateMachineComponent manages complex animation transitions.
 * Loads clips from GLTF sources and orchestrates state-based playback with
 * parameter-driven transitions and one-shot animation support.
 */
import * as THREE from 'three';

import { AnimationClipCache } from '../../animation/AnimationClipCache.js';
import {
  collectValidNodeNames,
  filterClipTracks,
  getTrackNodeName
} from '../../animation/AnimationClipUtils.js';
import {
  ANIMATION_CONFIG_RESOURCE_EXTENSION,
  AnimationConfigResource,
  isAnimationConfigResourcePath,
  isLegacyAnimationConfigPath,
} from '../../animation/AnimationConfigResource.js';
import { AnimationEventProcessor } from '../../animation/AnimationEventProcessor.js';
import { loadExternalAnimationSources } from '../../animation/ExternalAnimationLoader.js';
import { OneShotController } from '../../animation/OneShotController.js';
import { resolveRolesToBones, type SkeletonProfile } from '../../animation/SkeletonProfile.js';
import { TransitionConditionCompiler } from '../../animation/TransitionConditionCompiler.js';
import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { NON_SPATIAL, SceneComponent } from '../SceneComponent.js';

import { ModelMeshComponent } from './ModelMeshComponent.js';

import type {
  AnimationClipOptions,
  AnimationGraphId,
  AnimationGraphRuntime,
  AnimationLayerConfig,
  AnimationParameterValue,
  AnimationState,
  AnimationStateMachineConfig,
  AnimationStateMachineOptions,
  ExternalAnimationSource,
  ParameterMap,
  RetriggerPolicy
} from '../../animation/AnimationTypes.js';
import type { OneShotHost } from '../../animation/OneShotController.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';

export type {
  AnimationClipOptions,
  AnimationEventMarker,
  AnimationEventPayload,
  AnimationLayerConfig,
  AnimationLayerDefinition,
  AnimationLayerMask,
  AnimationState,
  AnimationStateMachineConfig,
  AnimationStateMachineOptions,
  AnimationStateTransition,
  ConditionPredicate,
  ExternalAnimationSource,
  ParameterMap,
  TransitionCondition
} from '../../animation/AnimationTypes.js';

@EngineClass('AnimationStateMachineComponent')
export class AnimationStateMachineComponent extends SceneComponent implements IAsyncLoadable {
  static readonly [NON_SPATIAL] = true;

  readonly [IAsyncLoadableTag] = true;

  //====================================================================================================
  // Serialized Configuration
  //====================================================================================================
  @property({ required: true})
  private configUrl: string = '';

  //====================================================================================================
  // Configuration and External Sources
  //====================================================================================================
  private gltfComponent: ModelMeshComponent | null = null;
  private config: AnimationStateMachineConfig | null = null;
  private externalAnimationSources: Array<string | ExternalAnimationSource> = [];
  private targetSkeletonProfile: SkeletonProfile | null = null;
  private graphRuntimes: Map<AnimationGraphId, AnimationGraphRuntime> = new Map([
    ['base', {
      id: 'base',
      kind: 'base',
      config: {
        id: 'base',
        initialState: null,
        states: {},
        enabled: true,
        weight: 1,
        mode: 'additive',
        policy: {
          applyBlendDurationOnEnter: true,
          fadeOutDuration: 0.3,
          retriggerPolicy: 'loopOnce',
        },
      },
      currentState: null,
      activeActionNames: [],
      selectedClipByState: new Map(),
    }],
  ]);
  private clipOptions: { [clipName: string]: AnimationClipOptions } = {};
  private oneShotClips: Set<string> = new Set();

  //====================================================================================================
  // Runtime State
  //====================================================================================================
  private parameters: Map<string, AnimationParameterValue> = new Map();
  private oneShotController!: OneShotController;
  private clipFinishedFlag: boolean = false;
  private isInitialized: boolean = false;
  private dirty: number = 0;
  private static readonly DIRTY_GRAPHS = 0b001;
  private static readonly DIRTY_WEIGHTS = 0b010;
  private static readonly DIRTY_OVERRIDE = 0b100;

  //====================================================================================================
  // Animation Runtime Objects
  //====================================================================================================
  private mixer: THREE.AnimationMixer | null = null;
  private actions: Map<string, THREE.AnimationAction> = new Map();
  private maskedActions: Map<string, THREE.AnimationAction> = new Map();
  private layerActions: Map<string, THREE.AnimationAction> = new Map();
  private baseActionWeights: Map<string, number> = new Map();
  private overrideableBones: Set<string> = new Set();
  private originalClips: Map<string, THREE.AnimationClip> = new Map();

  //====================================================================================================
  // Caches and Scratch Buffers
  //====================================================================================================
  private validNodeNamesCache: Set<string> | null = null;
  private clipLookupCache: Map<string, THREE.AnimationClip> = new Map();
  private layerWeightScratch: Map<string, number> = new Map();
  private blendActionScratch: Set<string> = new Set();
  private baseActionNamesScratch: string[] = [];
  private static readonly ACTION_WEIGHT_EPSILON = 0.001;

  //====================================================================================================
  // Helpers and Diagnostics
  //====================================================================================================
  private conditionCompiler = new TransitionConditionCompiler();
  private eventProcessor = new AnimationEventProcessor();
  private _asyncTaskWorker = new AsyncTaskWorker();
  /** Emits when an animation event marker is crossed at runtime. */
  public onAnimationEvent = this.eventProcessor.onAnimationEvent;

  //====================================================================================================
  // Lifecycle and Load
  //====================================================================================================
  public override initialize(options?: AnimationStateMachineOptions): void {
    super.initialize(options);
    this.configUrl = options?.configUrl ?? this.configUrl;
  }

  private getBaseCurrentState(): string | null {
    return this.getGraphRuntime('base')?.currentState ?? null;
  }

  private getBaseStates(): Record<string, AnimationState> {
    return this.getGraphRuntime('base')?.config.states ?? {};
  }

  private getGraphRuntime(graphId: AnimationGraphId): AnimationGraphRuntime | null {
    return this.graphRuntimes.get(graphId) ?? null;
  }

  private getLayerRuntime(): AnimationGraphRuntime | null {
    return this.getGraphRuntime('layer');
  }

  private async _loadAnim(): Promise<void> {
    if (!this.configUrl) {
      return;
    }
    const configPath = new AssetPath(this.configUrl);
    this.config = await this.loadAnimationConfig(configPath);
    if (!this.config) {
      console.error(`AnimationStateMachineComponent: Failed to load animation config from ${this.configUrl}`);
      return;
    }


    // Initialize component state from loaded config
    if (this.config.parameters) {
      for (const [key, value] of Object.entries(this.config.parameters)) {
        this.parameters.set(key, value);
      }
    }
    const baseRuntime = this.getGraphRuntime('base');
    if (baseRuntime) {
      baseRuntime.config.states = this.config.states ?? {};
      baseRuntime.config.initialState = this.config.initialState ?? null;
      baseRuntime.currentState = baseRuntime.config.initialState;
    }
    if (this.config.clipOptions) {
      this.clipOptions = this.config.clipOptions;
    }
    if (this.config.oneShotClips) {
      this.oneShotClips = new Set(this.config.oneShotClips);
    }
    if (this.config.externalAnimationSources) {
      this.externalAnimationSources = this.config.externalAnimationSources;
    }
    this.eventProcessor.setMarkers(
      this.config.events ? this.eventProcessor.sanitize(this.config.events) : {}
    );

    this.gltfComponent = this.getActor()?.getComponent(ModelMeshComponent) ?? null;
    if (!this.gltfComponent) {
      console.error('AnimationStateMachineComponent: No ModelMeshComponent found in actor.', this.getActor()?.name);
    }

    // Load target skeleton profile if retargeting is enabled
    if (this.config.targetSkeletonProfile) {
      try {
        const profilePath = new AssetPath(this.config.targetSkeletonProfile);
        this.targetSkeletonProfile = await resourceManager.loadJsonObject(profilePath);
        if (!this.targetSkeletonProfile) {
          console.error(`AnimationStateMachineComponent: Failed to load target skeleton profile from ${this.config.targetSkeletonProfile}`);
        }
      } catch (e) {
        console.error('AnimationStateMachineComponent: Failed to load target skeleton profile:', e);
      }
    }

    // Load external animation sources - but only after the GLTF is loaded
    // This will be handled in setupAnimations() when the GLTF mesh is ready

    if (this.gltfComponent) {
      this.gltfComponent.onMeshLoaded.add((meshComponent, mesh) => {
        this._asyncTaskWorker.runTask(async () => { await this.setupAnimations(); });
      });
      if (this.gltfComponent.isModelLoaded()) {
        await this.setupAnimations();
      }
    } else {
      // No GLTF component found, mark as initialized since setup is complete
      this.isInitialized = true;
    }
  }

  private async loadAnimationConfig(configPath: AssetPath): Promise<AnimationStateMachineConfig | null> {
    if (isAnimationConfigResourcePath(configPath.initialPath)) {
      const resourceData = await resourceManager.loadResource<AnimationConfigResource>(configPath);
      if (!resourceData?.instance) {
        return null;
      }

      if (resourceData.instance instanceof AnimationConfigResource) {
        return resourceData.instance.toConfig();
      }

      console.error(
        'AnimationStateMachineComponent: Loaded resource is not an AnimationConfigResource:',
        resourceData.class
      );
      return null;
    }

    return await resourceManager.loadJsonObject(configPath);
  }

  public override getConfigurationWarnings(): string[] {
    const warnings = super.getConfigurationWarnings();
    const url = this.configUrl.trim();
    if (url && isLegacyAnimationConfigPath(url)) {
      const migratedUrl = url.replace(/\.anim\.json$/i, ANIMATION_CONFIG_RESOURCE_EXTENSION);
      warnings.push(
        `Uses legacy '.anim.json' animation config (${url}). Migrate to '${migratedUrl}' (${ANIMATION_CONFIG_RESOURCE_EXTENSION}) resource format.`
      );
    }
    return warnings;
  }

  public override beginPlay(): void {
    super.beginPlay();

    // Reset initialization state
    this.isInitialized = false;

    this._asyncTaskWorker.runTask(async () => { await this._loadAnim(); });
  }

  // start IAsyncLoadable implementation
  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }
  // end IAsyncLoadable implementation

  private addAnimation(clip: THREE.AnimationClip, animationNameOverride?: string): THREE.AnimationAction {
    const loopMap = {
      LoopOnce: THREE.LoopOnce,
      LoopRepeat: THREE.LoopRepeat,
      LoopPingPong: THREE.LoopPingPong,
    } as const;
    const blendModeMap = {
      NormalAnimationBlendMode: THREE.NormalAnimationBlendMode,
      AdditiveAnimationBlendMode: THREE.AdditiveAnimationBlendMode,
    } as const;

    const clipName = animationNameOverride ?? clip.name;
    if (!this.originalClips.has(clipName)) {
      this.originalClips.set(clipName, clip.clone());
    }

    let processedClip = clip;

    // Filter tracks if a GLTF component and model are available
    if (this.validNodeNamesCache) {
      processedClip = filterClipTracks(processedClip, this.validNodeNamesCache);
    }

    const action = this.mixer!.clipAction(processedClip);
    // Apply user-defined options for this clip
    const opts = this.clipOptions[processedClip.name]; // Use processedClip.name in case it was recreated
    if (opts) {
      if (opts.loop) {
        const loopType = loopMap[opts.loop];
        if (loopType !== undefined) {
          action.setLoop(loopType, Infinity);
        }
      }
      if (opts.clampWhenFinished) {
        action.clampWhenFinished = opts.clampWhenFinished;
      }
      if (opts.blendMode) {
        const blendMode = blendModeMap[opts.blendMode];
        if (blendMode !== undefined) {
          action.blendMode = blendMode;
        }
      }
    }
    this.actions.set(clipName, action);
    return action;
  }

  private resolveConfiguredLayer(): AnimationLayerConfig | null {
    const masks = this.config?.masks ?? {};
    const layer = this.config?.layer;
    if (!layer) {
      return null;
    }
    if (!layer.mask) {
      console.warn('AnimationStateMachineComponent: layer.mask is required when layer config is provided.');
      return null;
    }
    if (!layer.states || Object.keys(layer.states).length === 0) {
      console.warn('AnimationStateMachineComponent: layer.states is required and must be non-empty.');
      return null;
    }
    if (!layer.initialState || !layer.states[layer.initialState]) {
      console.warn('AnimationStateMachineComponent: layer.initialState must reference an existing layer state.');
      return null;
    }

    const mask = layer.mask ? masks[layer.mask] : undefined;
    if (!mask) {
      console.warn(`AnimationStateMachineComponent: Layer mask '${layer.mask}' not found in config.masks.`);
      return null;
    }

    // Effective selectorType: layer overrides mask, default to 'bone'
    const selectorType = layer.selectorType ?? mask.selectorType ?? 'bone';

    // Effective include list: layer overrides mask
    const include = layer.include ?? mask.include ?? [];

    // Resolve roles to bone names if selectorType is 'role'
    let resolvedInclude: string[] = include;
    if (selectorType === 'role') {
      if (!this.targetSkeletonProfile) {
        console.warn(
          `AnimationStateMachineComponent: Layer '${layer.id}' uses selectorType 'role' but no targetSkeletonProfile is available. ` +
          'Skipping layer setup. Add targetSkeletonProfile to your config to use role-based masks.'
        );
        return null;
      }
      const { resolved, unresolved } = resolveRolesToBones(
        include,
        this.targetSkeletonProfile,
        `AnimationStateMachineComponent (layer: ${layer.id}, mask: ${layer.mask})`
      );
      resolvedInclude = resolved;
      if (unresolved.size > 0) {
        // Already warned in resolveRolesToBones
      }
    }

    return {
      ...layer,
      mode: layer.mode ?? 'additive',
      include: resolvedInclude,
      selectorType: 'bone', // After resolution, it's always bone names
      enabled: layer.enabled ?? true,
    };
  }

  private getLayerActionName(clip: string, reversed?: boolean): string {
    return reversed ? `${clip}#Reversed` : clip;
  }

  private getLayerActionSpecs(layerConfig: AnimationLayerConfig): Array<{ actionName: string; clipName: string; reversed: boolean }> {
    const specs = new Map<string, { actionName: string; clipName: string; reversed: boolean }>();
    for (const state of Object.values(layerConfig.states)) {
      if (state.clip) {
        if (typeof state.clip === 'string') {
          const actionName = this.getLayerActionName(state.clip, false);
          specs.set(actionName, { actionName, clipName: state.clip, reversed: false });
        } else if (Array.isArray(state.clip)) {
          for (const clipName of state.clip) {
            const actionName = this.getLayerActionName(clipName, false);
            specs.set(actionName, { actionName, clipName, reversed: false });
          }
        }
      }
      if (state.blend) {
        for (const entry of state.blend) {
          const reversed = entry.reversed ?? false;
          const actionName = this.getLayerActionName(entry.clip, reversed);
          specs.set(actionName, { actionName, clipName: entry.clip, reversed });
        }
      }
    }
    return Array.from(specs.values());
  }

  private createMaskedVariants(validNodeNames: Set<string>): void {
    if (!this.mixer || this.overrideableBones.size === 0) return;

    for (const [clipName, action] of this.actions.entries()) {
      const originalClip = this.originalClips.get(clipName);
      if (!originalClip) continue;

      const hasOverrideableTracks = originalClip.tracks.some((track) => this.overrideableBones.has(getTrackNodeName(track.name)));
      if (!hasOverrideableTracks) continue;

      const maskedClip = filterClipTracks(originalClip, validNodeNames, undefined, this.overrideableBones);

      const maskedAction = this.mixer.clipAction(maskedClip);
      maskedAction.setEffectiveWeight(0);
      maskedAction.enabled = true;
      maskedAction.play();
      maskedAction.time = action.time;

      this.maskedActions.set(clipName, maskedAction);
    }
  }

  private async setupAnimations(): Promise<void> {
    if (!this.gltfComponent) return;
    if (this.isInitialized) return; // Already initialized, avoid duplicate setup

    if (!this.gltfComponent.isModelLoaded()) return;

    if (this.externalAnimationSources.length > 0) {
      await loadExternalAnimationSources(
        this.externalAnimationSources,
        this.targetSkeletonProfile,
        this.gltfComponent
      );
    }

    this.mixer = new THREE.AnimationMixer(this.gltfComponent);
    this.oneShotController = new OneShotController(this as unknown as OneShotHost);
    this.resetRuntimeMaps();
    this.validNodeNamesCache = collectValidNodeNames(this.gltfComponent);

    this.loadBaseClips(this.gltfComponent.getAnimations());
    this.setupLayerGraph();
    this.finalizeSetup();
    this.isInitialized = true;
  }

  private resetRuntimeMaps(): void {
    this.actions.clear();
    this.maskedActions.clear();
    this.layerActions.clear();
    this.graphRuntimes.delete('layer');
    this.baseActionWeights.clear();
    this.overrideableBones.clear();
    this.originalClips.clear();
    this.clipLookupCache.clear();
  }

  private loadBaseClips(modelClips: THREE.AnimationClip[]): void {
    const stateClipNames = new Set<string>();
    for (const state of Object.values(this.getBaseStates())) {
      if (state.clip) {
        if (typeof state.clip === 'string') {
          stateClipNames.add(state.clip);
        } else if (Array.isArray(state.clip)) {
          state.clip.forEach(name => stateClipNames.add(name));
        }
      }
      if (state.blend) {
        state.blend.forEach(b => stateClipNames.add(b.clip));
      }
    }
    for (const oneShotClipName of this.oneShotClips) {
      stateClipNames.add(oneShotClipName);
    }

    for (const clip of modelClips) {
      this.clipLookupCache.set(clip.name, clip);
      if (!AnimationClipCache.instance.hasClip(clip.name)) {
        AnimationClipCache.instance.addClip(clip);
      }
    }

    for (const clipName of stateClipNames) {
      let clip = AnimationClipCache.instance.getClip(clipName);
      if (!clip) {
        clip = this.clipLookupCache.get(clipName);
        if (clip) {
          AnimationClipCache.instance.addClip(clip);
        }
      }
      if (clip) {
        this.addAnimation(clip);
      } else {
        console.warn(`AnimationStateMachineComponent: Clip '${clipName}' not found in cache or model.`);
      }
    }

    const allCachedClips = AnimationClipCache.instance.getClipNames();
    for (const clipName of allCachedClips) {
      if (!this.actions.has(clipName)) {
        const clip = AnimationClipCache.instance.getClip(clipName);
        if (clip) {
          this.addAnimation(clip);
        }
      }
    }
  }

  private setupLayerGraph(): void {
    const configuredLayer = this.resolveConfiguredLayer();
    if (!configuredLayer) return;

    const layerSpecs = this.getLayerActionSpecs(configuredLayer);
    const layerAffectedBones = new Set<string>();

    for (const spec of layerSpecs) {
      let sourceClip = AnimationClipCache.instance.getClip(spec.clipName);
      sourceClip ??= this.clipLookupCache.get(spec.clipName);
      if (!sourceClip) {
        console.warn(`AnimationStateMachineComponent: Layer clip '${spec.clipName}' not found.`);
        continue;
      }

      const filteredClip = filterClipTracks(
        sourceClip,
        this.validNodeNamesCache ?? new Set<string>(),
        configuredLayer.include,
        undefined // excludeBones removed - include-only model
      );
      for (const track of filteredClip.tracks) {
        layerAffectedBones.add(getTrackNodeName(track.name));
      }
      if (filteredClip.tracks.length === 0) {
        console.warn(`AnimationStateMachineComponent: Layer clip '${spec.clipName}' has no valid tracks after mask filtering.`);
        continue;
      }

      const actionClip = filteredClip.clone();
      const action = this.mixer!.clipAction(actionClip);
      action.blendMode = configuredLayer.mode === 'override'
        ? THREE.NormalAnimationBlendMode
        : THREE.AdditiveAnimationBlendMode;
      const layerClipOptions = this.clipOptions[spec.clipName];
      if (layerClipOptions?.loop) {
        if (layerClipOptions.loop === 'LoopOnce') {
          action.setLoop(THREE.LoopOnce, 1);
        } else if (layerClipOptions.loop === 'LoopRepeat') {
          action.setLoop(THREE.LoopRepeat, Infinity);
        } else if (layerClipOptions.loop === 'LoopPingPong') {
          action.setLoop(THREE.LoopPingPong, Infinity);
        }
      }
      if (layerClipOptions?.clampWhenFinished !== undefined) {
        action.clampWhenFinished = layerClipOptions.clampWhenFinished;
      }
      action.timeScale = spec.reversed ? -1 : 1;
      action.setEffectiveWeight(0);
      action.enabled = configuredLayer.enabled;
      action.play();
      this.layerActions.set(spec.actionName, action);
    }

    if (this.layerActions.size > 0 && layerAffectedBones.size > 0) {
      this.graphRuntimes.set('layer', {
        id: 'layer',
        kind: 'layer',
        config: {
          id: configuredLayer.id,
          states: configuredLayer.states,
          initialState: configuredLayer.initialState,
          enabled: configuredLayer.enabled,
          weight: Math.max(0, Math.min(1, configuredLayer.weight ?? 0)),
          mode: configuredLayer.mode,
          policy: {
            applyBlendDurationOnEnter: true,
            fadeOutDuration: 0.2,
            retriggerPolicy: 'loopOnce',
          },
        },
        currentState: configuredLayer.initialState,
        activeActionNames: [],
        selectedClipByState: new Map(),
      });

      if (configuredLayer.mode === 'override') {
        for (const bone of layerAffectedBones) {
          this.overrideableBones.add(bone);
        }
      }
    }
  }

  private finalizeSetup(): void {
    this.createMaskedVariants(this.validNodeNamesCache ?? new Set<string>());
    const layerRuntime = this.getLayerRuntime();
    if (layerRuntime) {
      this.applyGraphStateActions(layerRuntime);
    }
    this.applyOverrideBlend();
    this.conditionCompiler.precompileAll(this.getBaseStates());
    if (layerRuntime) {
      this.conditionCompiler.precompileAll(layerRuntime.config.states);
    }
    this.playInitialState();
  }

  private playInitialState(): void {
    const baseRuntime = this.getGraphRuntime('base');
    if (!baseRuntime) {
      return;
    }
    this.applyGraphStateActions(baseRuntime, {
      enteringState: true,
      previousActionNames: [],
      retriggerPolicy: baseRuntime.config.policy.retriggerPolicy,
    });
    this.applyOverrideBlend();
  }

  private getPreferredWeightedAction(actions: Array<THREE.AnimationAction | undefined>): THREE.AnimationAction | undefined {
    return actions.find((action) =>
      !!action
      && action.enabled
      && action.getEffectiveWeight() > AnimationStateMachineComponent.ACTION_WEIGHT_EPSILON
    );
  }

  private getPreferredAction(actions: Array<THREE.AnimationAction | undefined>): THREE.AnimationAction | undefined {
    const weighted = this.getPreferredWeightedAction(actions);
    if (weighted) {
      return weighted;
    }
    return actions.find((action) => !!action && action.enabled && action.isRunning());
  }

  private play(
    clip?: string,
    blendDuration: number = 0.3,
    preferredPreviousAction?: THREE.AnimationAction
  ): THREE.AnimationAction | null {
    if (!this.mixer) return null;
    if (!clip) return null;
    const action = this.actions.get(clip);
    if (action) {
      // Find the currently playing action (if any)
      // Check for one-shot action first, then fall back to active state actions
      const oneShotAction = (this.oneShotController?.isPlaying && this.oneShotController.currentClipName)
        ? this.actions.get(this.oneShotController.currentClipName)
        : undefined;
      const prevAction = oneShotAction
        ?? preferredPreviousAction
        ?? this.getPreferredAction(this.getActiveActions());
      if (prevAction && prevAction !== action) {
        prevAction.crossFadeTo(action, blendDuration, false);
      }
      action.reset().play();
      this.baseActionWeights.set(clip, 1);
      this.eventProcessor.resetActionTime(action);
      const maskedAction = this.maskedActions.get(clip);
      if (maskedAction) {
        maskedAction.reset();
        maskedAction.time = action.time;
        maskedAction.play();
      }
      // Listen for finished event to set the flag
      action.getMixer().addEventListener('finished', this._onClipFinished);
      this.applyOverrideBlend();
      return action;
    }
    return null;
  }

  //====================================================================================================
  // Public API - Parameters and Graph Control
  //====================================================================================================
  /**
   * Set one or more animation parameters. If called with (key, value), sets a single parameter.
   * If called with an object, sets multiple parameters at once and only evaluates the state machine once.
   *
   * If the component is not yet initialized, parameters will be stored and applied once initialization completes.
   */
  public setParameter(key: string | Record<string, number | boolean | string>, value?: number | boolean | string): void {
    // Skip parameter updates if disabled
    if (!this.enabled) {
      return;
    }

    // If not initialized yet, warn and ignore parameter changes
    if (!this.isInitialized) {
      if (typeof key === 'object' && key !== null) {
        const keys = Object.keys(key).join(', ');
        console.warn(`AnimationStateMachineComponent not yet initialized. Ignoring setParameter call for: ${keys}`);
      } else if (typeof key === 'string') {
        console.warn(`AnimationStateMachineComponent not yet initialized. Ignoring setParameter call for: ${key}`);
      }
      return;
    }

    let changed = false;
    if (typeof key === 'object' && key !== null) {
      for (const [k, v] of Object.entries(key)) {
        if (!this.parameters.has(k)) {
          console.error(`Parameter '${k}' does not exist in AnimationStateMachineComponent.`);
          continue;
        }
        if (this.parameters.get(k) !== v) {
          this.parameters.set(k, v);
          changed = true;
        }
      }
    } else if (typeof key === 'string') {
      if (!this.parameters.has(key)) {
        console.error(`Parameter '${key}' does not exist in AnimationStateMachineComponent.`);
        return;
      }
      const nextValue = value as number | boolean | string;
      if (this.parameters.get(key) !== nextValue) {
        this.parameters.set(key, nextValue);
        changed = true;
      }
    }

    if (changed) {
      this.dirty |= AnimationStateMachineComponent.DIRTY_GRAPHS
        | AnimationStateMachineComponent.DIRTY_WEIGHTS
        | AnimationStateMachineComponent.DIRTY_OVERRIDE;
    }
  }

  /**
   * Get an animation parameter
   */
  public getParameter(key: string): number | boolean | string {
    if (!this.parameters.has(key)) {
      if (!this.isInitialized) {
        throw new Error(`AnimationStateMachineComponent not yet initialized. Cannot get parameter '${key}'.`);
      }
      throw new Error(`Parameter '${key}' does not exist in AnimationStateMachineComponent.`);
    }
    return this.parameters.get(key)!;
  }

  /**
   * Get all parameters as a plain object
   */
  public getAllParameters(): ParameterMap {
    return Object.fromEntries(this.parameters.entries());
  }

  /**
   * Get animation parameters as a readonly map for debugging and tooling.
   */
  public getParametersMap(): ReadonlyMap<string, AnimationParameterValue> {
    return this.parameters;
  }

  /**
   * Get the currently active state name for a graph runtime.
   */
  public getGraphState(graphId: AnimationGraphId): string | null {
    return this.getGraphRuntime(graphId)?.currentState ?? null;
  }

  /**
   * Transition the specified graph directly to a state.
   */
  public transitionGraphToState(graphId: AnimationGraphId, state: string): void {
    const graphRuntime = this.getGraphRuntime(graphId);
    if (!graphRuntime) {
      console.warn(`AnimationStateMachineComponent: Graph '${graphId}' not found.`);
      return;
    }
    this.applyTransition(graphRuntime, state);
  }

  /**
   * Set graph weight.
   */
  public setGraphWeight(graphId: AnimationGraphId, weight: number): void {
    if (!this.isInitialized) {
      return;
    }
    const graphRuntime = this.getGraphRuntime(graphId);
    if (!graphRuntime) {
      console.warn(`AnimationStateMachineComponent: Graph '${graphId}' not found.`);
      return;
    }
    const clampedWeight = Math.max(0, Math.min(1, weight));
    if (graphRuntime.config.weight !== clampedWeight) {
      graphRuntime.config.weight = clampedWeight;
      this.dirty |= AnimationStateMachineComponent.DIRTY_WEIGHTS | AnimationStateMachineComponent.DIRTY_OVERRIDE;
    }
  }

  /**
   * Get graph weight.
   */
  public getGraphWeight(graphId: AnimationGraphId): number {
    if (!this.isInitialized) {
      return 0;
    }
    return this.getGraphRuntime(graphId)?.config.weight ?? 0;
  }

  /**
   * Set graph enabled state.
   */
  public setGraphEnabled(graphId: AnimationGraphId, enabled: boolean): void {
    if (!this.isInitialized) {
      return;
    }
    const graphRuntime = this.getGraphRuntime(graphId);
    if (!graphRuntime) {
      console.warn(`AnimationStateMachineComponent: Graph '${graphId}' not found.`);
      return;
    }
    if (graphRuntime.config.enabled !== enabled) {
      graphRuntime.config.enabled = enabled;
      this.dirty |= AnimationStateMachineComponent.DIRTY_WEIGHTS | AnimationStateMachineComponent.DIRTY_OVERRIDE;
    }
  }

  /**
   * Get all one-shot clip names.
   */
  public getOneShotClipNames(): string[] {
    return Array.from(this.oneShotClips);
  }

  /**
   * Get all registered animation actions as a readonly map.
   */
  public getActionsMap(): ReadonlyMap<string, THREE.AnimationAction> {
    return this.actions;
  }

  /**
   * Get all registered clip names.
   */
  public getClipNames(): string[] {
    return Array.from(this.actions.keys());
  }

  /**
   * Returns the currently active animation actions based on the current state.
   */
  public getActiveActions(): THREE.AnimationAction[] {
    const currentStateName = this.getBaseCurrentState();
    if (!currentStateName) return [];
    const state = this.getBaseStates()[currentStateName];
    if (!state) return [];
    return this.getActionNamesForState(state)
      .map((name) => this.actions.get(name))
      .filter((action): action is THREE.AnimationAction => !!action);
  }

  /**
   * Gets whether the animation state machine has finished initializing
   * (external animations loaded, parameters set up, and animations configured).
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Check if a parameter exists
   */
  public hasParameter(key: string): boolean {
    return this.parameters.has(key);
  }

  //====================================================================================================
  // Graph Evaluation and State Application
  //====================================================================================================
  private resolveClipFromState(clip: string | string[]): string | null {
    if (typeof clip === 'string') return clip;
    if (Array.isArray(clip) && clip.length > 0) {
      return clip[Math.floor(Math.random() * clip.length)] ?? null;
    }
    return null;
  }

  private getActionNamesForState(state: AnimationState): string[] {
    if (state.blend && state.blend.length > 0) {
      return state.blend.map((b) => b.reversed ? `${b.clip}#Reversed` : b.clip);
    }
    if (typeof state.clip === 'string') return [state.clip];
    if (Array.isArray(state.clip)) return state.clip.filter((c): c is string => typeof c === 'string');
    return [];
  }

  private getActiveBaseActionNames(): string[] {
    const baseRuntime = this.getGraphRuntime('base');
    if (!baseRuntime) {
      this.baseActionNamesScratch.length = 0;
      return this.baseActionNamesScratch;
    }
    return baseRuntime.activeActionNames;
  }

  private getCompositeOverrideWeight(): number {
    if (this.oneShotController?.isPlaying) {
      return 0;
    }
    const layerRuntime = this.getLayerRuntime();
    if (!layerRuntime) return 0;
    if (layerRuntime.config.mode !== 'override') return 0;
    if (!layerRuntime.config.enabled) return 0;
    return layerRuntime.config.weight;
  }

  private applyOverrideBlend(): void {
    const overrideWeight = this.getCompositeOverrideWeight();
    if (this.overrideableBones.size === 0) {
      return;
    }

    const baseActionNames = this.getActiveBaseActionNames();
    for (const actionName of baseActionNames) {
      const fullAction = this.actions.get(actionName);
      if (!fullAction) continue;

      const maskedAction = this.maskedActions.get(actionName);
      if (!maskedAction) continue;

      const baseTargetWeight = this.baseActionWeights.get(actionName);
      const fallbackWeight = fullAction.isRunning() ? 1 : 0;
      const totalWeight = Math.max(0, baseTargetWeight ?? fallbackWeight);

      fullAction.setEffectiveWeight(totalWeight * (1 - overrideWeight));
      maskedAction.setEffectiveWeight(totalWeight * overrideWeight);
      maskedAction.time = fullAction.time;
      if (!maskedAction.isRunning()) {
        maskedAction.play();
      }
    }
  }

  private applyTransition(graphRuntime: AnimationGraphRuntime, nextState: string): boolean {
    const states = graphRuntime.config.states;
    if (!states[nextState]) return false;
    const fromState = graphRuntime.currentState;
    if (!fromState || fromState === nextState) {
      return false;
    }

    const policy = graphRuntime.config.policy;
    let blendDuration = policy.fadeOutDuration;
    const fromStateDef = states[fromState];
    const transition = fromStateDef?.transitions?.find((candidate) => candidate.to === nextState);
    if (transition && typeof transition.blendDuration === 'number') {
      blendDuration = transition.blendDuration;
    }
    const fadeOutDuration = policy.applyBlendDurationOnEnter ? blendDuration : policy.fadeOutDuration;
    const prevStateDef = states[fromState];
    const previousActionNames = prevStateDef ? this.getActionNamesForState(prevStateDef) : [];

    graphRuntime.currentState = nextState;
    graphRuntime.selectedClipByState.delete(nextState);
    const didEnter = this.applyGraphStateActions(graphRuntime, {
      blendDuration,
      enteringState: true,
      previousActionNames,
      retriggerPolicy: policy.retriggerPolicy,
    });
    if (!didEnter) {
      graphRuntime.currentState = fromState;
      return false;
    }

    for (const actionName of previousActionNames) {
      if (graphRuntime.kind === 'base') {
        const action = this.actions.get(actionName);
        if (action && action.isRunning()) {
          action.fadeOut(fadeOutDuration);
          this.baseActionWeights.set(actionName, 0);
        }
        const maskedAction = this.maskedActions.get(actionName);
        if (maskedAction && maskedAction.isRunning()) {
          maskedAction.fadeOut(fadeOutDuration);
        }
      } else {
        const action = this.layerActions.get(actionName);
        if (action && action.isRunning()) {
          action.fadeOut(fadeOutDuration);
        }
      }
    }

    this.dirty |= AnimationStateMachineComponent.DIRTY_WEIGHTS | AnimationStateMachineComponent.DIRTY_OVERRIDE;
    return true;
  }

  private evaluateAndTransitionGraph(graphRuntime: AnimationGraphRuntime): boolean {
    const currentState = graphRuntime.currentState;
    if (!currentState) return false;
    const states = graphRuntime.config.states;
    if (!states[currentState]) return false;

    const state = states[currentState];

    const globalState = states.__any__;
    if (globalState?.transitions?.length) {
      for (const candidate of globalState.transitions) {
        if (this.conditionCompiler.matches(candidate.condition, this.parameters, this.clipFinishedFlag)) {
          return this.applyTransition(graphRuntime, candidate.to);
        }
      }
    }

    for (const candidate of state.transitions) {
      if (this.conditionCompiler.matches(candidate.condition, this.parameters, this.clipFinishedFlag)) {
        return this.applyTransition(graphRuntime, candidate.to);
      }
    }
    return false;
  }

  private applyBaseBlendWeights(
    blend: Array<{ clip: string; weight?: number; weightParam?: string; reversed?: boolean }>
  ): boolean {
    if (!this.mixer) return false;
    let hasAnyAction = false;
    this.blendActionScratch.clear();
    // Start and set weights for blend actions
    blend.forEach(({ clip, weight, weightParam, reversed }) => {
      reversed ??= false;
      const animationName = reversed ? `${clip}#Reversed` : clip;
      let action: THREE.AnimationAction | undefined;
      if (typeof clip === 'string') {
        action = this.actions.get(animationName);
        if (!action) {
          const clipInstance = this.clipLookupCache.get(clip) ?? this.gltfComponent?.getAnimations().find(c => c.name === clip);
          if (clipInstance) {
            action = this.addAnimation(clipInstance.clone(), animationName);
            action.timeScale = reversed ? -1 : 1;
          }
        }
      }
      let w = weight ?? 0;
      if (weightParam) {
        const paramValue = this.getParameter(weightParam);
        if (typeof paramValue === 'number') {
          w = Math.max(0, Math.min(1, paramValue));
        }
      }
      if (!action) {
        return;
      }
      hasAnyAction = true;
      // reset the weights if this is the first time the action is seen
      if (!this.blendActionScratch.has(animationName)) {
        this.blendActionScratch.add(animationName);
        action.setEffectiveWeight(0);
      }
      // use accumulated weights in case the action is reused in the blend
      w += action.getEffectiveWeight();
      action.setEffectiveWeight(w);
      this.baseActionWeights.set(animationName, w);
      action.enabled = true;
      if (!action.isRunning()) {
        action.play();
      }
      const maskedAction = this.maskedActions.get(animationName);
      if (maskedAction) {
        maskedAction.setEffectiveWeight(0);
        maskedAction.enabled = true;
        maskedAction.time = action.time;
        if (!maskedAction.isRunning()) {
          maskedAction.play();
        }
      }
    });
    this.dirty |= AnimationStateMachineComponent.DIRTY_OVERRIDE;
    return hasAnyAction;
  }

  private shouldRestartActionOnStateEnter(
    actionName: string,
    action: THREE.AnimationAction,
    retriggerPolicy: RetriggerPolicy
  ): boolean {
    if (retriggerPolicy === 'always') {
      return true;
    }
    if (retriggerPolicy === 'never') {
      return false;
    }
    const hashIdx = actionName.indexOf('#');
    const clipName = hashIdx >= 0 ? actionName.slice(0, hashIdx) : actionName;
    const clipOpts = this.clipOptions[clipName];
    if (clipOpts?.loop === 'LoopOnce') {
      return true;
    }
    return action.loop === THREE.LoopOnce;
  }

  private applyGraphStateActions(
    graphRuntime: AnimationGraphRuntime,
    options: {
      blendDuration?: number;
      enteringState?: boolean;
      previousActionNames?: string[];
      retriggerPolicy?: RetriggerPolicy;
    } = {}
  ): boolean {
    const {
      blendDuration = graphRuntime.config.policy.fadeOutDuration,
      enteringState = false,
      previousActionNames = [],
      retriggerPolicy = 'loopOnce',
    } = options;
    const currentStateName = graphRuntime.currentState;
    if (!currentStateName) {
      graphRuntime.activeActionNames = [];
      return false;
    }
    const currentState = graphRuntime.config.states[currentStateName];
    if (!currentState) {
      graphRuntime.activeActionNames = [];
      return false;
    }

    if (graphRuntime.kind === 'base') {
      if (currentState.blend && currentState.blend.length > 0) {
        graphRuntime.activeActionNames = currentState.blend.map((b) => b.reversed ? `${b.clip}#Reversed` : b.clip);
        return this.applyBaseBlendWeights(currentState.blend);
      }

      graphRuntime.activeActionNames = [];
      if (!enteringState || !currentState.clip) {
        if (currentState.clip) {
          const selectedClip = graphRuntime.selectedClipByState.get(currentStateName);
          graphRuntime.activeActionNames = selectedClip
            ? [selectedClip]
            : this.getActionNamesForState(currentState);
        }
        return true;
      }

      let selectedClip = graphRuntime.selectedClipByState.get(currentStateName) ?? null;
      if (!selectedClip) {
        selectedClip = this.resolveClipFromState(currentState.clip);
        if (selectedClip) {
          graphRuntime.selectedClipByState.set(currentStateName, selectedClip);
        }
      }
      if (!selectedClip) {
        return false;
      }

      graphRuntime.activeActionNames = [selectedClip];
      const preferredPreviousAction = this.getPreferredAction(
        previousActionNames.map((actionName) => this.actions.get(actionName))
      );
      return this.play(
        selectedClip,
        blendDuration,
        preferredPreviousAction
      ) !== null;
    }

    if (this.oneShotController?.isPlaying) {
      for (const action of this.layerActions.values()) {
        action.enabled = false;
        action.setEffectiveWeight(0);
      }
      graphRuntime.activeActionNames = [];
      return true;
    }

    const globalWeight = graphRuntime.config.enabled ? graphRuntime.config.weight : 0;
    this.layerWeightScratch.clear();

    if (currentState.blend && currentState.blend.length > 0) {
      for (const entry of currentState.blend) {
        const actionName = this.getLayerActionName(entry.clip, entry.reversed ?? false);
        let w = entry.weight ?? 0;
        if (entry.weightParam) {
          const paramValue = this.getParameter(entry.weightParam);
          if (typeof paramValue === 'number') {
            w = Math.max(0, Math.min(1, paramValue));
          }
        }
        this.layerWeightScratch.set(actionName, (this.layerWeightScratch.get(actionName) ?? 0) + w);
      }
    } else if (currentState.clip) {
      let selectedClip = graphRuntime.selectedClipByState.get(currentStateName) ?? null;
      if (!selectedClip) {
        selectedClip = this.resolveClipFromState(currentState.clip);
        if (selectedClip) {
          graphRuntime.selectedClipByState.set(currentStateName, selectedClip);
        }
      }
      if (selectedClip) {
        this.layerWeightScratch.set(this.getLayerActionName(selectedClip, false), 1);
      }
    }

    graphRuntime.activeActionNames = Array.from(this.layerWeightScratch.keys());
    for (const [actionName, action] of this.layerActions.entries()) {
      const local = this.layerWeightScratch.get(actionName) ?? 0;
      action.enabled = graphRuntime.config.enabled && local > 0;
      action.setEffectiveWeight(local * globalWeight);
      const shouldRestart = enteringState
        && action.enabled
        && this.shouldRestartActionOnStateEnter(actionName, action, retriggerPolicy);
      if (shouldRestart) {
        action.reset();
        if (action.timeScale < 0) {
          action.time = action.getClip().duration;
        }
      }
      if (action.enabled && (shouldRestart || !action.isRunning())) {
        action.play();
      }
    }
    return true;
  }

  private applyLayerStateActions(
    options: { enteringState?: boolean; retriggerPolicy?: RetriggerPolicy } = {}
  ): void {
    const layerRuntime = this.getLayerRuntime();
    if (!layerRuntime) {
      return;
    }
    this.applyGraphStateActions(layerRuntime, options);
  }

  /**
   * Evaluate all active state graphs and perform at most one transition per graph.
   */
  private evaluateStateGraphs(): boolean {
    if (!this.oneShotController || this.oneShotController.isPlaying) {
      this.clipFinishedFlag = false;
      return false;
    }
    let transitionApplied = false;

    const baseRuntime = this.getGraphRuntime('base');
    if (baseRuntime) {
      transitionApplied = this.evaluateAndTransitionGraph(baseRuntime) || transitionApplied;
    }
    const layerRuntime = this.getLayerRuntime();
    if (layerRuntime) {
      transitionApplied = this.evaluateAndTransitionGraph(layerRuntime) || transitionApplied;
    }

    this.clipFinishedFlag = false;
    return transitionApplied;
  }

  //====================================================================================================
  // Runtime Tick and Playback Controls
  //====================================================================================================
  /**
   * Blend to a new animation clip over a given duration (in seconds)
   */
  public blendTo(clip: string, duration: number = 0.3): void {
    if (!this.mixer) return;
    let nextAction = this.actions.get(clip);
    // Fade out all other actions instead of stopping them immediately
    for (const [name, action] of this.actions.entries()) {
      if (action !== nextAction) {
        action.fadeOut(duration);
      }
    }
    // Use local variables only, do not use this.currentAction
    if (nextAction) {
      nextAction.reset().play();
    }
  }

  private applyAllGraphWeights(): void {
    const baseRuntime = this.getGraphRuntime('base');
    if (baseRuntime) {
      this.applyGraphStateActions(baseRuntime);
    }
    const layerRuntime = this.getLayerRuntime();
    if (layerRuntime) {
      this.applyGraphStateActions(layerRuntime);
    }
  }

  /**
   * Called every frame after physics. Used to update state time and evaluate transitions.
   */
  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    if (!this.enabled) return;

    if (this.mixer) {
      this.mixer.update(deltaTime);
    }

    const needsEval = this.dirty !== 0 || this.clipFinishedFlag;
    if (needsEval) {
      if ((this.dirty & AnimationStateMachineComponent.DIRTY_GRAPHS) !== 0 || this.clipFinishedFlag) {
        this.evaluateStateGraphs();
      }
      if ((this.dirty & AnimationStateMachineComponent.DIRTY_WEIGHTS) !== 0) {
        this.applyAllGraphWeights();
      }
      if ((this.dirty & AnimationStateMachineComponent.DIRTY_OVERRIDE) !== 0) {
        this.applyOverrideBlend();
      }
      this.dirty = 0;
      if (this.mixer) {
        this.mixer.update(0);
      }
    }

    this.eventProcessor.process(this.getEventActions());
  }

  /**
   * Override the enabled setter to clear animations when disabled.
   * When set to false, all animations are cleared and no further processing occurs.
   */
  public override set enabled(value: boolean) {
    if (super.enabled === value) return;

    super.enabled = value;

    if (!value) {
      this.clearAllAnimations();
    }
  }

  /**
   * Override the enabled getter.
   */
  public override get enabled(): boolean {
    return super.enabled;
  }

  /**
   * Clears all currently playing animations
   */
  private clearAllAnimations(): void {
    if (!this.mixer) return;

    // Stop and fade out all actions
    for (const [name, action] of this.actions.entries()) {
      if (action.isRunning()) {
        action.fadeOut(0.1); // Quick fade out
        action.stop();
      }
    }

    for (const action of this.maskedActions.values()) {
      if (action.isRunning()) {
        action.fadeOut(0.1);
        action.stop();
      }
    }

    for (const action of this.layerActions.values()) {
      if (action.isRunning()) {
        action.fadeOut(0.1);
        action.stop();
      }
    }

    // Reset state
    if (this.oneShotController) {
      this.oneShotController.reset();
    }
    this.clipFinishedFlag = false;
    this.eventProcessor.clearActionTimes();
    this.baseActionWeights.clear();
  }

  //====================================================================================================
  // One-shot Runtime
  //====================================================================================================
  /**
   * Trigger a one-shot animation by name. Returns a promise that resolves when the one-shot finishes.
   * @param clip The name of the one-shot animation clip to play
   * @param freezeOnLastFrame If true, the animation will freeze on the last frame and not resume the state machine
   */
  public playOneShot(clip: string, freezeOnLastFrame: boolean = false): Promise<void> {
    if (!this.oneShotController) return Promise.resolve();
    return this.oneShotController.playOneShot(clip, freezeOnLastFrame);
  }

  private _onClipFinished = (event: any) => {
    // Only set the flag if the finished action is the current state's clip (base or layer)
    if (!event?.action) return;
    const finishedClipName = event.action.getClip().name;
    const baseCurrentState = this.getBaseCurrentState();
    const currentState = baseCurrentState ? this.getBaseStates()[baseCurrentState] : undefined;
    const layerRuntime = this.getLayerRuntime();
    const layerState = layerRuntime?.currentState
      ? layerRuntime.config.states[layerRuntime.currentState]
      : undefined;
    const selectedLayerClip = layerRuntime?.currentState
      ? layerRuntime.selectedClipByState.get(layerRuntime.currentState) ??
        (typeof layerState?.clip === 'string' ? layerState.clip : null)
      : null;

    const clipCandidates = [
      { clip: currentState?.clip, selectedClip: null },
      { clip: layerState?.clip, selectedClip: selectedLayerClip },
    ];
    this.clipFinishedFlag = clipCandidates.some(({ clip, selectedClip }) =>
      this.doesClipMatch(clip, finishedClipName, selectedClip),
    );
  };

  //====================================================================================================
  // Event Action Selection
  //====================================================================================================
  private doesClipMatch(
    clip: string | string[] | undefined | null,
    finishedClipName: string,
    selectedClip: string | null = null,
  ): boolean {
    if (!clip) return false;
    if (selectedClip === finishedClipName) return true;
    if (typeof clip === 'string') return clip === finishedClipName;
    return clip.includes(finishedClipName);
  }

  private getEventActions(): THREE.AnimationAction[] {
    if (this.oneShotController?.isPlaying && this.oneShotController.currentClipName) {
      const oneShotAction = this.actions.get(this.oneShotController.currentClipName);
      return oneShotAction ? [oneShotAction] : [];
    }
    const baseCurrentState = this.getBaseCurrentState();
    const currentState = baseCurrentState ? this.getBaseStates()[baseCurrentState] : undefined;
    if (currentState?.blend) {
      const blendedActions = currentState.blend
        .map((blendEntry) => {
          const animationName = blendEntry.reversed ? `${blendEntry.clip}#Reversed` : blendEntry.clip;
          return this.actions.get(animationName);
        })
        .filter((action): action is THREE.AnimationAction => !!action);
      if (blendedActions.length > 0) {
        return blendedActions;
      }
    }

    const activeActions = this.getActiveActions();
    if (activeActions.length > 0) return activeActions;

    return Array.from(this.actions.values()).filter(action => action.isRunning() && action.getEffectiveWeight() > 0.001);
  }


  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_AnimationMachine';
  }
}
